import { useEffect } from 'react';
import { useStore } from 'zustand';
import { X } from 'lucide-react';
import { toastStore, type ToastItem } from '@shared/lib/toast';
import { cn } from '@shared/lib';

const VARIANT: Record<ToastItem['variant'], string> = {
  default: 'border-border',
  success: 'border-success/50',
  danger: 'border-danger/50',
};

export function Toaster() {
  const toasts = useStore(toastStore, (s) => s.toasts);
  const dismiss = useStore(toastStore, (s) => s.dismiss);

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className="pointer-events-none fixed top-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      role="alert"
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-lg border bg-surface-raised p-3 shadow-lg',
        VARIANT[toast.variant],
      )}
    >
      <div className="flex-1">
        <p className="text-sm font-medium text-fg">{toast.title}</p>
        {toast.description ? (
          <p className="mt-0.5 text-xs text-muted">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="rounded text-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
